// Phase 43 (item 5 / D21-D24 + PHASE-WIDE CORRECTNESS) — conflict-of-interest &
// duplicate-client matching for party-add and client-add.
//
// Normalization here is DELIBERATELY STRONGER than lib/orphanDedup.mjs (the
// "Database cleanup" matcher, which stays trim+lowercase so it and its server
// mirror in routes/clients.js keep agreeing). A Belgian professional-conduct
// conflict check must not be defeated by an accent or stray spacing, so this
// path strips diacritics (NFD + combining-mark removal) AND collapses internal
// whitespace, on top of trim+lowercase — so "Dupont" == "Dupónt" == "  du  pont".
//
// Two keys, per the brief:
//   • COI (opponent / additional-opponent vs the client pool) — NAME ONLY,
//     email OPTIONAL/ignored (D23). Assistive warning, never authoritative.
//   • DUPLICATE (new client vs existing clients) — STRICT: name AND email.
//
// PURE: no React, no DOM, no network — loads unchanged in the Vite frontend and
// in a plain `node` smoke harness (same rationale as lib/orphanDedup.mjs).

// Accent-strip + whitespace-collapse + trim + lowercase.
export function normName(s) {
  return (s == null ? "" : String(s))
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "") // drop combining diacritical marks
    .replace(/\s+/g, " ") // collapse internal whitespace runs to one space
    .trim()
    .toLowerCase();
}

// Emails: no internal spaces are meaningful; strip all whitespace + lowercase.
export function normEmail(s) {
  return (s == null ? "" : String(s)).replace(/\s+/g, "").toLowerCase();
}

// ── Phase 43 CORRECTION (item 5 / D29) — normalized SUBSTRING name match ──────
// A conduct COI check (and the live duplicate typeahead, item 6) must surface
// "helios" ↔ "Helios NV", which exact whole-name matching missed (RQ12). Match is
// CONTAINS in either direction on the normalized name TEXT — UNTYPED (no co::/in::
// prefix) so an individual "Helios" still surfaces a company "Helios NV". Higher
// false-positive rate accepted: over-warning is the safe failure mode.

// Normalized full-name text: company → companyName; individual → "first last".
// "" when there is no name basis (never matches).
export function nameText(p) {
  if (!p) return "";
  const isCompany = (p.type || "individual") === "company";
  if (isCompany) return normName(p.companyName);
  return normName([p.firstName, p.lastName].filter(Boolean).join(" "));
}

// Substring match in EITHER direction; both sides must be non-empty.
export function nameMatches(aText, bText) {
  if (!aText || !bText) return false;
  return aText.includes(bText) || bText.includes(aText);
}

// Members of `pool` whose name substring-matches `party`. opts.excludeId skips
// the row itself (edit). Used by the save-time COI/duplicate backstop AND the
// live typeahead — one matcher, both surfaces.
export function findNameMatches(party, pool, opts) {
  const excludeId = opts && opts.excludeId;
  const key = nameText(party);
  if (!key) return [];
  const out = [];
  for (const c of pool || []) {
    if (excludeId != null && c.id === excludeId) continue;
    if (nameMatches(key, nameText(c))) out.push(c);
  }
  return out;
}

// Name-only identity key for a person/company row (client OR party). Company →
// companyName; individual → "last||first". Type defaults to individual. Returns
// "" when the name basis is blank, so a blank never matches another blank.
export function coiNameKey(p) {
  if (!p) return "";
  const isCompany = (p.type || "individual") === "company";
  if (isCompany) {
    const co = normName(p.companyName);
    return co ? "co::" + co : "";
  }
  const last = normName(p.lastName);
  const first = normName(p.firstName);
  if (!last && !first) return "";
  return "in::" + last + "||" + first;
}

// COI (assistive): every client in `clients` whose NAME matches `party`
// (name-only, email ignored — D23). Caller supplies the pool; for a real COI
// check that pool must be archived-INCLUSIVE. Returns matching client rows.
export function findClientNameMatches(party, clients) {
  const key = coiNameKey(party);
  if (!key) return [];
  const out = [];
  for (const c of clients || []) if (coiNameKey(c) === key) out.push(c);
  return out;
}

// DUPLICATE (strict): existing clients matching `candidate` on name AND email.
// A blank email never participates (mirrors the cleanup email-gate, but with
// accent/space-normalized name). `opts.excludeId` skips the row itself on edit.
export function findClientDuplicates(candidate, clients, opts) {
  const excludeId = opts && opts.excludeId;
  const key = coiNameKey(candidate);
  const email = normEmail(candidate && candidate.email);
  if (!key || !email) return [];
  const out = [];
  for (const c of clients || []) {
    if (excludeId != null && c.id === excludeId) continue;
    if (coiNameKey(c) === key && normEmail(c.email) === email) out.push(c);
  }
  return out;
}

// Batch dedup for the import path (no per-row modal — D / "import COI/dedup at
// scale"): for each imported candidate row, find its strict duplicates in the
// existing client pool. Returns [{ candidate, matches }] for candidates with
// >= 1 match, for a reviewable completion summary.
export function batchClientDuplicates(candidates, existing) {
  const out = [];
  for (const cand of candidates || []) {
    const matches = findClientDuplicates(cand, existing);
    if (matches.length) out.push({ candidate: cand, matches });
  }
  return out;
}
